import { useEffect } from 'react';

export interface ShortcutHandlers {
  onNewChat?: () => void;
  onFocusInput?: () => void;
  /** Open chat history search (⌘/Ctrl+K) */
  onSearch?: () => void;
  onToggleSidebar?: () => void;
  onOpenProviders?: () => void;
  onOpenWorkspace?: () => void;
  onOpenProject?: () => void;
  onStop?: () => void;
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

/**
 * Global keyboard shortcuts.
 * ⌘/Ctrl+N new chat · ⌘/Ctrl+K search · ⌘/Ctrl+B sidebar
 * ⌘/Ctrl+, settings (privacy) · ⌘/Ctrl+. providers · Esc stop
 */
export function useKeyboardShortcuts(handlers: ShortcutHandlers, enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;

      // Esc always available to stop generation
      if (e.key === 'Escape' && handlers.onStop) {
        handlers.onStop();
        return;
      }

      if (!mod) return;

      const key = e.key.toLowerCase();

      // Don't steal chord keys while typing except intentional global chords
      if (
        isTypingTarget(e.target) &&
        key !== 'k' &&
        key !== 'n' &&
        key !== 'b'
      ) {
        return;
      }

      if (key === 'n' && handlers.onNewChat) {
        e.preventDefault();
        handlers.onNewChat();
        return;
      }
      if (key === 'k') {
        e.preventDefault();
        if (handlers.onSearch) handlers.onSearch();
        else handlers.onFocusInput?.();
        return;
      }
      if (key === 'b' && handlers.onToggleSidebar) {
        e.preventDefault();
        handlers.onToggleSidebar();
        return;
      }
      if (key === ',' && handlers.onOpenWorkspace) {
        e.preventDefault();
        handlers.onOpenWorkspace();
        return;
      }
      if (key === '.' && handlers.onOpenProviders) {
        e.preventDefault();
        handlers.onOpenProviders();
        return;
      }
      if (key === '/' && !isTypingTarget(e.target) && handlers.onOpenProject) {
        e.preventDefault();
        handlers.onOpenProject();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlers, enabled]);
}
